import { useState } from "react"
import { motion } from "framer-motion"
import { Cloud, Presentation, Sparkles, ArrowLeft, ShieldCheck, Minus, Plus, CreditCard, Lock } from "lucide-react"
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface Plan {
  id: string
  name: string
  monthlyPrice: number
  yearlyPrice: number
  popular?: boolean
  [key: string]: any
}

interface PlanCheckoutProps {
  plan: Plan
  billingCycle: "monthly" | "yearly"
  onBack: () => void
}

const addOnDefs = [
  {
    id: "cloud-storage",
    name: "Cloud Storage",
    description: "Select the storage limit you need to add to your plan",
    monthlyPrice: 9,
    yearlyPrice: 84,
    storageOptions: ["30 GB", "50 GB", "100 GB", "200 GB"],
    attendeesOptions: [] as string[],
    unitLabel: "Storage",
    icon: <Cloud className="w-5 h-5 text-primary" />
  },
  {
    id: "webinar",
    name: "Webinar",
    description: "Select the attendees limit you need to add to your plan",
    monthlyPrice: 84,
    yearlyPrice: 838,
    storageOptions: [] as string[],
    attendeesOptions: ["500", "1000", "2000", "5000"],
    unitLabel: "Attendees",
    icon: <Presentation className="w-5 h-5 text-primary" />
  },
  {
    id: "ai-summary",
    name: "AI Generated Summary",
    description: "Our service connects spoken words to written text",
    monthlyPrice: 20,
    yearlyPrice: 200,
    storageOptions: [] as string[],
    attendeesOptions: [] as string[],
    unitLabel: "",
    icon: <Sparkles className="w-5 h-5 text-primary" />
  }
]

export function PlanCheckout({ plan, onBack }: PlanCheckoutProps) {
  const t = useTranslation()
  const [hosts, setHosts] = useState(1)
  const localBillingCycle = "yearly" // Fixed to yearly only
  const [checkedAddOns, setCheckedAddOns] = useState<string[]>([])

  const toggleAddon = (id: string) => {
    setCheckedAddOns((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const planPrice = plan.yearlyPrice // Always use yearly price

  const addonTotal = checkedAddOns.reduce((sum, id) => {
    const def = addOnDefs.find((a) => a.id === id)!
    return sum + def.yearlyPrice // Always use yearly price
  }, 0)

  const subtotal = planPrice * hosts + addonTotal
  const tax = Math.round(subtotal * 0.18 * 100) / 100
  const total = subtotal + tax

  const ADDON_ICONS: Record<string, { bg: string; gradient: string }> = {
    "cloud-storage": { bg: "bg-cyan-50 dark:bg-cyan-900/20", gradient: "from-cyan-400 to-blue-500" },
    webinar: { bg: "bg-orange-50 dark:bg-orange-900/20", gradient: "from-orange-400 to-rose-500" },
    "ai-summary": { bg: "bg-violet-50 dark:bg-violet-900/20", gradient: "from-violet-400 to-purple-600" },
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 pt-2 pb-4">

      {/* ─── Breadcrumb ───────────────────────────── */}
      <div className="flex items-center gap-2 text-sm mb-6">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors font-medium">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("licenses.pricing")}
        </button>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground font-semibold">{t("licenses.checkout")}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

        {/* ─── Left: Plan Configuration + Add-ons ── */}
        <div className="lg:col-span-2 space-y-6">

          {/* Plan card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-card dark:bg-card border border-slate-200 dark:border-border dark:border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Color header */}
            <div className={`h-2 w-full ${plan.popular ? "bg-gradient-to-r from-primary to-indigo-600" : "bg-gradient-to-r from-amber-400 to-orange-500"}`} />
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{plan.name} Plan</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t("licenses.configurePlan")} <button className="text-primary hover:underline font-medium">{t("licenses.exploreOtherPlans")}</button>
                  </p>
                </div>
                {/* Quantity controls */}
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">{t("licenses.hosts")}</p>
                    <div className="flex items-center border border-slate-200 dark:border-border dark:border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setHosts(h => Math.max(1, h - 1))}
                        className="w-8 h-8 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-muted transition-colors"
                      ><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-10 text-center text-sm font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{hosts}</span>
                      <button
                        onClick={() => setHosts(h => h + 1)}
                        className="w-8 h-8 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-muted transition-colors"
                      ><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing cycle - Yearly Only */}
              <div>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">{t("licenses.billingCycle")}</p>
                <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("licenses.yearlyBilling")}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">${plan.yearlyPrice}/yr</p>
                  </div>
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t("licenses.yearlyDiscount")}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Add-ons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-card dark:bg-card border border-slate-200 dark:border-border dark:border-border rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <h4 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("licenses.superchargeAddOns")}</h4>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t("licenses.optionalExtras")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {addOnDefs.map((addon, index) => {
                const isChecked = checkedAddOns.includes(addon.id)
                const addonStyle = ADDON_ICONS[addon.id] ?? { bg: "bg-primary/10", gradient: "from-primary to-indigo-500" }

                return (
                  <motion.div
                    key={addon.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.1 }}
                    className={`relative group cursor-pointer rounded-xl border transition-all duration-200 p-5 h-full
                      ${isChecked
                        ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-md ring-1 ring-primary/20"
                        : "border-slate-200 dark:border-border dark:border-border hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md bg-card dark:bg-card"
                      }`}
                    onClick={() => toggleAddon(addon.id)}
                  >
                    {/* Checkbox */}
                    <div className="absolute top-4 right-4">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                        ${isChecked 
                          ? "border-primary bg-primary" 
                          : "border-slate-300 dark:border-slate-600 group-hover:border-primary/50"
                        }`}
                      >
                        {isChecked && <div className="w-2 h-2 bg-card rounded-sm" />}
                      </div>
                    </div>

                    {/* Icon + Content */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl ${addonStyle.bg} flex items-center justify-center shrink-0 shadow-sm`}>
                        {addon.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-bold text-slate-900 dark:text-foreground/80 dark:text-white text-base leading-tight">{addon.name}</h5>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{addon.description}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-center mt-auto">
                      <span className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
                        ${addon.yearlyPrice}
                        <span className="text-xs font-normal text-slate-400">/yr</span>
                      </span>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>

        {/* ─── Right: Order Summary ─────────────── */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-card p-4 dark:bg-card border border-slate-200 dark:border-border dark:border-border rounded-2xl shadow-sm hover:shadow-md transition-shadow sticky top-6"
        >
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("licenses.orderSummary")}</h4>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("licenses.reviewSelection")}</p>
          </div>

          <div className="space-y-4 mb-6">
            {/* Plan line */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white truncate">{plan.name} Plan</p>
                <p className="text-xs text-slate-400 mt-0.5">{hosts} {t("licenses.host")}{hosts > 1 ? 's' : ''} · {localBillingCycle}</p>
              </div>
              <span className="text-sm font-bold text-slate-900 dark:text-foreground/80 dark:text-white shrink-0">${planPrice * hosts}</span>
            </div>

            {/* Selected add-ons */}
            {checkedAddOns.map((id) => {
              const def = addOnDefs.find((a) => a.id === id)!
              const addonPrice = def.yearlyPrice
              return (
                <div key={id} className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white truncate">{def.name}</p>
                    <p className="text-xs text-slate-400">yearly</p>
                  </div>
                  <span className="text-sm font-bold text-slate-900 dark:text-foreground/80 dark:text-white shrink-0">${addonPrice}</span>
                </div>
              )
            })}

            {/* Totals */}
            <div className="border-t border-slate-200 dark:border-border dark:border-border pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">{t("licenses.subtotal")}</span>
                <span className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">${subtotal}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">{t("licenses.tax")} (18%)</span>
                <span className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">${tax}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-border dark:border-border">
                <span className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("licenses.total")}</span>
                <span className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Checkout button */}
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 px-4 bg-gradient-to-r from-primary to-primary/90 text-white rounded-xl font-bold text-sm hover:from-primary/90 hover:to-primary/80 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            {t("licenses.completePurchase")} • ${total.toFixed(2)}
          </motion.button>

          {/* Security note */}
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500 dark:text-slate-400">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span>{t("licenses.secureCheckout")}</span>
          </div>
        </motion.div>
      </div>
      </div>
    </div>
  )
}

